import React, { useCallback, useContext, useEffect, useState } from "react";
import Cookies from "universal-cookie";
import { IUserFields } from "../types";
import APIService from "src/services/Api";

const cookies = new Cookies();

const readStoredSession = () => {
  const authToken = localStorage.getItem("authToken");
  const cookieAuth = cookies.get("isAuthenticated");
  const valid = !!(authToken && cookieAuth);

  return {
    valid,
    user: valid ? ((cookies.get("userdata") as IUserFields | null) ?? null) : null,
  };
};

export const clearAuthSession = () => {
  cookies.remove("userdata", { path: "/" });
  cookies.remove("isAuthenticated", { path: "/" });
  localStorage.removeItem("authToken");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("rememberUser");
};

export interface AuthContextInterface {
  token: string | null;
  expiresAt: number | null;
  isAuthenticated: boolean;
  user: IUserFields | null;
  activateAccount: () => void;
  updateUserData: () => void;
  setUserData: (
    id: string,
    email: string,
    fullname: string,
    phone: string,
    country: string,
    birthday: string,
    verifyAccount: boolean,
    role: string,
    verifiedIdentity: boolean,
    createdAt: string,
    photoUrl: string,
    nickname: string,
    lastConnection: string
  ) => void;
  logout: () => void;
}

export const authContextDefaults: AuthContextInterface = {
  expiresAt: null,
  token: null,
  isAuthenticated: false,
  user: null,
  setUserData: () => null,
  logout: () => null,
  activateAccount: () => null,
  updateUserData: () => null,
};

interface AuthProviderOptions {
  children: React.ReactElement;
}

export const AuthProvider = ({ children }: AuthProviderOptions) => {
  const initialSession = readStoredSession();
  const [isAuthenticated, setisAuthenticated] = useState(initialSession.valid);
  const [expiresAt, setExpiresAt] = useState(null);
  const [token, setToken] = useState<string | null>(
    initialSession.valid ? localStorage.getItem("authToken") : null
  );
  const [user, setUser] = useState<IUserFields | null>(initialSession.user);

  const logout = useCallback(() => {
    clearAuthSession();
    setisAuthenticated(false);
    setUser(null);
    setToken(null);
    setExpiresAt(null);
  }, []);

  useEffect(() => {
    const session = readStoredSession();
    if (!session.valid) {
      clearAuthSession();
      setisAuthenticated(false);
      setUser(null);
      setToken(null);
      return;
    }
    setisAuthenticated(true);
    setUser(session.user);
    setToken(localStorage.getItem("authToken"));
  }, []);

  const updateUserData = async () => {
    const query = await APIService.getUserInfo({ userId: user?.id || "" });
    const userData = query.data.data.user;
    if (userData?.id) {
      setUserData(
        userData.id,
        userData.email,
        userData.fullname,
        userData.phone,
        userData.country,
        userData.birthday,
        userData.verifyAccount,
        userData.role,
        userData.verifiedIdentity,
        userData?.createdAt || "",
        userData?.photoUrl || "",
        userData?.nickname || "",
        userData?.lastConnection || ""
      );
    }
  };

  const setUserData = (
    id: string,
    email: string,
    fullname: string,
    phone: string,
    country: string,
    birthday: string,
    verifyAccount: boolean,
    role: string,
    verifiedIdentity: boolean,
    createdAt: string,
    photoUrl: string,
    nickname: string,
    lastConnection: string
  ) => {
    cookies.set(
      "userdata",
      {
        id,
        email,
        fullname,
        phone,
        country,
        birthday,
        verifyAccount,
        role,
        verifiedIdentity,
        createdAt,
        photoUrl,
        nickname,
        lastConnection,
      },
      { path: "/" }
    );

    cookies.set("isAuthenticated", true, { path: "/" });

    setisAuthenticated(true);
    setToken(localStorage.getItem("authToken"));
    setUser({
      id,
      email,
      fullname,
      phone,
      country,
      birthday,
      verifyAccount,
      role,
      verifiedIdentity,
      createdAt,
      photoUrl,
      nickname,
      lastConnection,
    });
  };

  const activateAccount = () => {
    if (isAuthenticated && user) {
      setUserData(
        user?.id || "",
        user?.email || "",
        user?.fullname || "",
        user?.phone || "",
        user?.country || "",
        user?.birthday || "",
        user?.verifyAccount || false,
        user?.role || "user",
        user?.verifiedIdentity || false,
        user?.createdAt || "",
        user?.photoUrl || "",
        user?.nickname || "",
        user?.lastConnection || ""
      );
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        expiresAt,
        token,
        setUserData,
        updateUserData,
        logout,
        activateAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthContext = React.createContext<AuthContextInterface>(authContextDefaults);

export const UseAuth = () => useContext(AuthContext);

export const hasValidAuthSession = () => readStoredSession().valid;
